import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBackendAuth } from '../contexts/BackendAuthContext';
import { ROUTES, ROLES } from '../utils/constants';
import { HOME_ALLOWED_ROLES, isOperatorShellRole, resolveEffectiveRole } from '../utils/sessionRole';
import { useAlgoDeviceSupport } from '../pages/Admin/algoDeviceSupport';
import { shouldRedirectToOnboard } from '../utils/agreementGate';
import { EVENT_SYSTEM_TAB_ROLES } from './eventSystemAccess';
import useRoleGuard from '../hooks/useRoleGuard';
import ErrorBoundary from '../components/common/ErrorBoundary';
import BackendProtectedRoute from '../components/common/BackendProtectedRoute';
import PageWrapper from '../components/common/PageWrapper';

// Pages
import LoginPage from '../pages/LoginPage';
import OnboardPage from '../pages/OnboardPage';
import DashboardPage from '../pages/DashboardPage';
import DemoDashboard from '../pages/1_DemoDashboard';
import AdminPage from '../pages/AdminPage';
import UserManagment from '../pages/UserManagment';
import TestToolsPage from '../pages/TestToolsPage';
import RestTestToolsPage from '../pages/Admin/RestTestToolsPage';
import UnauthorizedPage from '../pages/UnauthorizedPage';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SupportReportsPage from '../pages/SupportReportsPage';
import SupportedTenantsPage from '../pages/Admin/SupportedTenantsPage';
import SupportReportDetailPage from '../pages/SupportReportDetailPage';
import MyReportDetailPage from '../pages/MyReportDetailPage';
import SubscriptionSummaryPage from '../pages/SubscriptionSummaryPage';
import ReleaseNotesPage from '../pages/ReleaseNotesPage';
// New backend auth pages
import BackendLoginPage from '../pages/BackendLoginPage';
import BackendDashboardPage from '../pages/BackendDashboardPage';
// Admin API Test Page
import AdminTestPage from '../pages/AdminTestPage';
// Agent Dashboard Page
import AgentDashboardPage from '../pages/AgentDashboardPage';
// Agent Management Page (V2 with backend integration and tenant scoping)
import AgentManagementPage from '../pages/Admin/AgentManagementPage';
import {
  TouchPanelListPage,
  TouchPanelDesignerPage,
} from '../pages/Admin/TouchPanelDesigner';
import TrainingPage from '../pages/Training/TrainingPage';
import LessonPage from '../pages/Training/LessonPage';
import HomeLauncherPage from '../pages/HomeLauncher';
import LauncherSettingsPage from '../pages/HomeLauncher/LauncherSettingsPage';
import AmbientPage from '../pages/AmbientPage';
// Admin Event System Pages
const UnifiedFlowsPage = lazy(() => import('../pages/Admin/UnifiedFlowsPage'));
const ActionsPage = lazy(() => import('../pages/Admin/ActionsPage'));
const DevicesPage = lazy(() => import('../pages/Admin/DevicesPage'));
const LogsPage = lazy(() => import('../pages/Admin/LogsPage'));
const IncomingEventsPage = lazy(() => import('../pages/Admin/IncomingEventsPage'));
import TenantManagementPage from '../pages/Admin/TenantManagementPage';
import SupportWorkspacePage, { LegacyTenantDetailRedirect } from '../pages/Admin/SupportWorkspacePage';
import BillingPage from '../pages/Admin/BillingPage';
import PricingPage from '../pages/Admin/PricingPage';
import DealerManagementPage from '../pages/Admin/DealerManagementPage';
import AllUsersDirectoryPage from '../pages/Admin/AllUsersDirectoryPage';
import BackupRecoveryPage from '../pages/Admin/BackupRecoveryPage';
import DealerDetailPage from '../pages/Admin/DealerDetailPage';
import DealerWorkspacePage from '../pages/Dealer/DealerWorkspacePage';
import { useDealerAdmin } from '../contexts/DealerAdminContext';
import { dealerAdminReturnPath, isDealerAllowedPath, isDealerExcludedPath } from '../pages/Admin/dealerAdminNav';
import { isSupportExperiencePath, readSupportedTenantSession } from '../pages/Admin/supportedTenantsSession';
import {
  rememberPathForDealerLogin,
  resolvePostAuthPath,
  resolveRootRedirect,
  consumeDealerReturnPath,
} from '../utils/dealerHostRouting';
import { BILLING_ALLOWED_ROLES } from '../pages/Admin/billingAccess';
import { canOpenPricingPage, hasPlatformPermission } from '../pages/Admin/platformAccess';
import VoiceSettingsPage from '../pages/Admin/VoiceSettingsPage';
import VoiceDashboardPage from '../pages/Admin/VoiceDashboardPage';
// Inbound Rules Admin Page
const InboundRulesPage = lazy(() => import('../pages/Admin/InboundRulesPage'));
const ScheduledTriggersPage = lazy(() => import('../pages/Admin/ScheduledTriggersPage'));
// Paging Admin Page (route still /simple-actions)
import SimpleActionsPage from '../pages/Admin/SimpleActionsPage';
// Call Routing Admin Page
import CallRoutingPage from '../pages/Admin/CallRoutingPage';
// SIP Interworking Admin Page
import SipInterworkingPage from '../pages/Admin/SipInterworkingPage';
// Algo Devices Admin Page
import AlgoDevicesPage from '../pages/Admin/AlgoDevicesPage';
// Config Editor Page
import ConfigEditorPage from '../pages/Admin/ConfigEditorPage';
import ConfigWorkspacePage from '../pages/Admin/ConfigWorkspacePage';
import ConfigValidatePage from '../pages/Admin/ConfigValidatePage';
import ConfigSnapshotsPage from '../pages/Admin/ConfigSnapshotsPage';
import ConfigParsedPage from '../pages/Admin/ConfigParsedPage';
import SegmentHealthPage from '../pages/Admin/SegmentHealthPage';
import IncidentTypeListPage from '../pages/Admin/IncidentTypeListPage';
import IncidentTypeEditorPage from '../pages/Admin/IncidentTypeEditorPage';
import SitesListPage from '../pages/Admin/SitesListPage';
import WorkspaceListPage from '../pages/Admin/WorkspaceListPage';
import AdminShortcutsPage from '../pages/Admin/AdminShortcutsPage';
import WorkspaceDetailPage from '../pages/Admin/WorkspaceDetailPage';
import WorkspaceStudioPage from '../pages/WorkspaceStudio/WorkspaceStudioPage';
import CollaborationWorkspacePage from '../pages/Collaboration/CollaborationWorkspacePage';
import CollaborationWorkspaceListPage from '../pages/Admin/CollaborationWorkspaceListPage';
import CollaborationWorkspaceEditorPage from '../pages/Admin/CollaborationWorkspaceEditorPage';
import ContactListsPage from '../pages/Admin/ContactListsPage';
import AgreementsPage from '../pages/Admin/AgreementsPage';
import AskSbIntegrationsPage from '../pages/Admin/AskSbIntegrationsPage';

const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const RoleEditorPage = lazy(() => import('../pages/RoleEditorPage'));
const ActivityFeedPage = lazy(() => import('../pages/ActivityFeedPage'));

// Protected route wrapper with role-based access
const ProtectedRoute = ({ children, allowedRoles = [], allowPricing = false, platformPermission = '', requireAlgoDeviceSupport = false }) => {
  const { currentUser, authReady, userData, userClaims, agreementStatus, agreementStatusReady } = useAuth();
  const location = useLocation();
  const dealerAdmin = useDealerAdmin();
  const algoEnabled = useAlgoDeviceSupport();

  // Debug: Always log authReady
  // console.log('[ProtectedRoute] authReady:', authReady);

  // Show nothing while auth is initializing
  if (!authReady) return null;

  // Redirect to login if not authenticated
  if (!currentUser) {
    rememberPathForDealerLogin({
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      pathname: location.pathname,
    });
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!agreementStatusReady) {
    return <LoadingSpinner text="Checking account..." />;
  }

  if (shouldRedirectToOnboard({ pathname: location.pathname, agreementStatus })) {
    return <Navigate to={ROUTES.ONBOARD} replace />;
  }

  const userRole = resolveEffectiveRole(userClaims?.role, userData?.role);
  const access = { role: userRole, permissions: userData?.permissions };
  const pricingOk = allowPricing && canOpenPricingPage(access) && !dealerAdmin.active;
  const permissionNames = Array.isArray(platformPermission)
    ? platformPermission
    : (platformPermission ? [platformPermission] : []);
  const platformOk = permissionNames.some((key) => hasPlatformPermission(access, key)) && !dealerAdmin.active;
  const algoRouteOk = requireAlgoDeviceSupport && algoEnabled;
  const guardedRoles = (pricingOk || platformOk || algoRouteOk) ? [...allowedRoles, userRole].filter(Boolean) : allowedRoles;

  // Wait for role to be populated before making access decisions
  if (!userRole) {
    return <LoadingSpinner text="Loading permissions..." />;
  }

  // Debug logging for troubleshooting RBAC issues
  // console.log('[ProtectedRoute] userRole:', userRole);
  // console.log('[ProtectedRoute] allowedRoles:', allowedRoles);

  // Special handling for demo users
  if (userRole === ROLES.DEMO_USER && allowedRoles.includes(ROLES.DEMO_USER)) {
    // console.log('ProtectedRoute - Demo user has access');
    return children;
  }

  const hasAccess = useRoleGuard(guardedRoles);
  const dealerReturn = dealerAdminReturnPath({
    dealerId: dealerAdmin.dealerId,
    tenantId: dealerAdmin.tenantId,
  });

  if (requireAlgoDeviceSupport && !algoEnabled) {
    return <Navigate to={dealerAdmin.active ? dealerReturn : ROUTES.DASHBOARD} replace />;
  }

  if (dealerAdmin.active) {
    if (isDealerExcludedPath(location.pathname) || !isDealerAllowedPath(location.pathname)) {
      return <Navigate to={dealerReturn} replace />;
    }
    return children;
  }
  if (readSupportedTenantSession()?.tenantId) {
    if (!isSupportExperiencePath(location.pathname)) {
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    }
    return children;
  }
  // console.log('[ProtectedRoute] hasAccess:', hasAccess);

  // Only redirect if authReady is true and hasAccess is false
  if (authReady && !hasAccess) {
    return (
      <Navigate
        to={isOperatorShellRole(userRole) ? ROUTES.HOME_LAUNCHER : '/unauthorized'}
        replace
      />
    );
  }

  // Otherwise, render children if access is allowed
  return hasAccess ? children : null;
};

// Define route configurations with allowed roles
const routeConfigs = {
  [ROUTES.DASHBOARD]: {
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPERADMIN,
      ROLES.TENANT_ADMIN,
      ROLES.RESPONDER,
      ROLES.DEMO_USER,
      ROLES.SUPPORT_USER,
      ROLES.OVERSEER,
    ],
  },
  [ROUTES.ADMIN]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.SETTINGS]: { allowedRoles: [ROLES.DEMO_USER, ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.TEST_TOOLS]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.REST_TEST_TOOLS]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.ROLE_EDITOR]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.SUPPORT_REPORTS]: {
    allowedRoles: HOME_ALLOWED_ROLES,
  },
  [ROUTES.RELEASE_NOTES]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.ADMIN_AGREEMENTS]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.API_TEST_PAGE]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.AGENT_DASHBOARD]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.AGENT_MANAGEMENT]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.TOUCH_PANEL_DESIGNER]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.TOUCH_PANEL_EDITOR]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.ADMIN_ALGO_DEVICES]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  // Event System Routes
  [ROUTES.ADMIN_TEMPLATES]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.ADMIN_FLOWS]: { allowedRoles: EVENT_SYSTEM_TAB_ROLES },
  [ROUTES.ADMIN_ACTIONS]: { allowedRoles: EVENT_SYSTEM_TAB_ROLES },
  [ROUTES.ADMIN_DEVICES]: { allowedRoles: EVENT_SYSTEM_TAB_ROLES },
  [ROUTES.CALL_ROUTING]: { allowedRoles: EVENT_SYSTEM_TAB_ROLES },
  [ROUTES.SCHEDULED_TRIGGERS]: { allowedRoles: EVENT_SYSTEM_TAB_ROLES },
  [ROUTES.ADMIN_LOGS]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.ADMIN_TENANTS]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.ADMIN_TENANT_DETAIL]: { allowedRoles: [ROLES.SUPERADMIN] },
  [ROUTES.ADMIN_SUPPORT]: { allowedRoles: [ROLES.SUPERADMIN] },
  [ROUTES.ADMIN_BILLING]: { allowedRoles: BILLING_ALLOWED_ROLES },
  [ROUTES.ADMIN_PRICING]: { allowedRoles: [ROLES.SUPERADMIN] },
  [ROUTES.ADMIN_DEALERS]: { allowedRoles: [ROLES.SUPERADMIN] },
  [ROUTES.ADMIN_DEALER_DETAIL]: { allowedRoles: [ROLES.SUPERADMIN] },
  [ROUTES.ADMIN_ALL_USERS]: { allowedRoles: [ROLES.SUPERADMIN] },
  [ROUTES.ADMIN_BACKUP_RECOVERY]: { allowedRoles: [ROLES.SUPERADMIN] },
  [ROUTES.VOICE_SETTINGS]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.VOICE_DASHBOARD]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.CONFIG_EDITOR]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.CONFIG_WORKSPACE]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.CONFIG_SNAPSHOTS]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN] },
  [ROUTES.CONFIG_PARSED]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.SEGMENT_HEALTH]: { allowedRoles: [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN] },
  [ROUTES.ACTIVITY_FEED]: {
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPERADMIN,
      ROLES.TENANT_ADMIN,
      ROLES.RESPONDER,
      ROLES.OVERSEER,
      ROLES.SUPPORT_USER,
    ],
  },
  // Home, launcher, and My Reports are available to any signed-in tenant user
  [ROUTES.MY_REPORTS]: {
    allowedRoles: HOME_ALLOWED_ROLES,
  },
  [ROUTES.SUBSCRIPTION]: {
    allowedRoles: HOME_ALLOWED_ROLES,
  },
};

const TRAINING_ALLOWED_ROLES = [
  ROLES.TENANT_ADMIN,
  ROLES.OPERATOR,
  ROLES.SUPPORT_USER,
  ROLES.ADMIN,
  ROLES.SUPERADMIN,
];

function currentHostname() {
  return typeof window !== 'undefined' ? window.location.hostname : '';
}

function RootRedirect({ currentUser, agreementStatus }) {
  return (
    <Navigate
      to={resolveRootRedirect({
        hostname: currentHostname(),
        currentUser,
        agreementComplete: agreementStatus?.complete,
      })}
      replace
    />
  );
}

function PostAuthRedirect({ agreementStatus }) {
  return (
    <Navigate
      to={resolvePostAuthPath({
        hostname: currentHostname(),
        agreementComplete: agreementStatus?.complete,
        returnPath: consumeDealerReturnPath(),
      })}
      replace
    />
  );
}

const AppRoutes = ({ skipFirebaseAuthCheck = false }) => {
  const { currentUser, authReady, userClaims, agreementStatus } = useAuth();
  const location = useLocation();

  // Show loading state while auth is initializing
  // Skip this check for backend auth pages
  if (!authReady && !skipFirebaseAuthCheck) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  // Wrap routes with PageWrapper for smooth transitions
  const wrapWithPageWrapper = (element) => {
    return <PageWrapper>{element}</PageWrapper>;
  };

  return (
    <Routes location={location}>
      {/* Backend auth routes - these must be defined FIRST to take precedence */}
      <Route path={ROUTES.BACKEND_LOGIN} element={wrapWithPageWrapper(<BackendLoginPage />)} />
      <Route path={ROUTES.BACKEND_DASHBOARD} element={wrapWithPageWrapper(<BackendDashboardPage />)} />

      {/* Root path redirect */}
      <Route
        path="/"
        element={
          !authReady && !skipFirebaseAuthCheck
            ? <LoadingSpinner text="Loading..." />
            : <RootRedirect currentUser={currentUser} agreementStatus={agreementStatus} />
        }
      />

      {/* Public routes */}
      <Route
        path={ROUTES.ONBOARD}
        element={<OnboardPage />}
      />
      <Route
        path={ROUTES.LOGIN}
        element={currentUser ? <PostAuthRedirect agreementStatus={agreementStatus} /> : <LoginPage />}
      />

      {/* Tenant/Admin dashboard */}
      <Route
        path={ROUTES.DASHBOARD}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.DASHBOARD].allowedRoles}>
            {wrapWithPageWrapper(<DashboardPage />)}
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.HOME_LAUNCHER}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.MY_REPORTS].allowedRoles}>
            {wrapWithPageWrapper(<HomeLauncherPage />)}
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.AMBIENT}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.MY_REPORTS].allowedRoles}>
            <AmbientPage />
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.LAUNCHER_SETTINGS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.MY_REPORTS].allowedRoles}>
            <LauncherSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.TRAINING}
        element={
          <ProtectedRoute allowedRoles={TRAINING_ALLOWED_ROLES}>
            {wrapWithPageWrapper(<TrainingPage />)}
          </ProtectedRoute>
        }
      />

      <Route
        path={`${ROUTES.TRAINING}/lesson/:procedureId`}
        element={
          <ProtectedRoute allowedRoles={TRAINING_ALLOWED_ROLES}>
            {wrapWithPageWrapper(<LessonPage />)}
          </ProtectedRoute>
        }
      />

      <Route
        path="/collaboration/:workspaceId"
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.MY_REPORTS].allowedRoles}>
            <ErrorBoundary>
              <CollaborationWorkspacePage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN].allowedRoles}>
            {wrapWithPageWrapper(<Navigate to={ROUTES.ADMIN_FLOWS} replace />)}
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.SETTINGS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.SETTINGS].allowedRoles}>
            {wrapWithPageWrapper(
              <Suspense fallback={<LoadingSpinner />}>
                {userClaims?.role === 'demo_user' ? <SettingsPage /> : <SettingsPage />}
              </Suspense>
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.TEST_TOOLS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.TEST_TOOLS].allowedRoles}>
            {wrapWithPageWrapper(<TestToolsPage />)}
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.REST_TEST_TOOLS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.REST_TEST_TOOLS].allowedRoles}>
            <RestTestToolsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ROLE_EDITOR}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ROLE_EDITOR].allowedRoles}>
            <Suspense fallback={<LoadingSpinner />}>
              <RoleEditorPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Role Editor with user ID parameter */}
      <Route
        path={`${ROUTES.ROLE_EDITOR}/:userId`}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ROLE_EDITOR].allowedRoles}>
            <Suspense fallback={<LoadingSpinner />}>
              <RoleEditorPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* User Management route (admin & tenant_admin only) */}
      {/*
        When adding new roles (e.g., support_user, board_admin),
        update allowedRoles for /admin/user-management below.
      */}
      <Route
        path={ROUTES.USER_MANAGEMENT}
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <UserManagment pageTitle="Users" showBackButton />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_ASK_SB}
        element={
          <ProtectedRoute allowedRoles={[ROLES.TENANT_ADMIN, ROLES.SUPERADMIN]}>
            <ErrorBoundary>
              <AskSbIntegrationsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Support queue: tenant_admin (tenant), platform support/superadmin/overseer (global) */}
      <Route path={ROUTES.SUPPORTED_TENANTS} element={<SupportedTenantsPage />} />
      <Route path={ROUTES.SUPPORTED_TENANT} element={<SupportedTenantsPage />} />

      <Route
        path={ROUTES.SUPPORT_REPORTS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.SUPPORT_REPORTS].allowedRoles}>
            <ErrorBoundary>
              <SupportReportsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_AGREEMENTS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_AGREEMENTS].allowedRoles}>
            <ErrorBoundary>
              <AgreementsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Release Notes management route (admin only) */}
      <Route
        path={ROUTES.RELEASE_NOTES}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.RELEASE_NOTES].allowedRoles}>
            <ErrorBoundary>
              <ReleaseNotesPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Admin API Test Page - Using Regular Auth Protection */}
      <Route
        path={ROUTES.API_TEST_PAGE}
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <AdminTestPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Agent Dashboard Page - Using Regular Auth Protection */}
      <Route
        path={ROUTES.AGENT_DASHBOARD}
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <AgentDashboardPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Agent Management Page */}
      <Route
        path={ROUTES.AGENT_MANAGEMENT}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.AGENT_MANAGEMENT].allowedRoles} requireAlgoDeviceSupport>
            <ErrorBoundary>
              <AgentManagementPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Touch Panels — list + designer (Firestore, Phase 3) */}
      <Route
        path={ROUTES.TOUCH_PANEL_DESIGNER}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.TOUCH_PANEL_DESIGNER].allowedRoles}>
            <ErrorBoundary>
              <TouchPanelListPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TOUCH_PANEL_EDITOR}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.TOUCH_PANEL_EDITOR].allowedRoles}>
            <ErrorBoundary>
              <TouchPanelDesignerPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Algo Devices Admin Page */}
      <Route
        path={ROUTES.ADMIN_ALGO_DEVICES}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_ALGO_DEVICES].allowedRoles} requireAlgoDeviceSupport>
            <ErrorBoundary>
              <AlgoDevicesPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Config Editor Page */}
      <Route
        path={ROUTES.CONFIG_EDITOR}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.CONFIG_EDITOR].allowedRoles}>
            <ErrorBoundary>
              <ConfigEditorPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Config Workspace Page */}
      <Route
        path={ROUTES.CONFIG_WORKSPACE}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.CONFIG_WORKSPACE].allowedRoles}>
            <ErrorBoundary>
              <ConfigWorkspacePage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Config Validate Page */}
      <Route
        path="/admin/configs/:deviceId/validate"
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <ConfigValidatePage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Inbound Rules Admin Route */}
      <Route
        path={ROUTES.INBOUND_RULES}
        element={
          <ProtectedRoute allowedRoles={EVENT_SYSTEM_TAB_ROLES}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <InboundRulesPage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.SCHEDULED_TRIGGERS}
        element={
          <ProtectedRoute allowedRoles={EVENT_SYSTEM_TAB_ROLES}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ScheduledTriggersPage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Paging Admin Route */}
      <Route
        path={ROUTES.SIMPLE_ACTIONS}
        element={
          <ProtectedRoute allowedRoles={EVENT_SYSTEM_TAB_ROLES}>
            <ErrorBoundary>
              <SimpleActionsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Call Routing Admin Route */}
      <Route
        path={ROUTES.CALL_ROUTING}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.CALL_ROUTING].allowedRoles}>
            <ErrorBoundary>
              <CallRoutingPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* SIP Interworking Admin Route */}
      <Route
        path={ROUTES.SIP_INTERWORKING}
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <SipInterworkingPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Event System Admin Routes */}
      <Route
        path={ROUTES.CONFIG_SNAPSHOTS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.CONFIG_SNAPSHOTS].allowedRoles}>
            <ErrorBoundary>
              <ConfigSnapshotsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.CONFIG_PARSED}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.CONFIG_PARSED].allowedRoles}>
            <ErrorBoundary>
              <ConfigParsedPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_TEMPLATES}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_TEMPLATES].allowedRoles}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <UnifiedFlowsPage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_FLOWS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_FLOWS].allowedRoles}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <UnifiedFlowsPage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_ACTIONS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_ACTIONS].allowedRoles}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ActionsPage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_DEVICES}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_DEVICES].allowedRoles} requireAlgoDeviceSupport>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <DevicesPage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_LOGS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_LOGS].allowedRoles}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <LogsPage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_INCOMING_EVENTS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_LOGS].allowedRoles}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <IncomingEventsPage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Segment Health Page */}
      <Route
        path={ROUTES.SEGMENT_HEALTH}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.SEGMENT_HEALTH].allowedRoles}>
            <ErrorBoundary>
              <SegmentHealthPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Activity Feed Page (structured event timeline) */}
      <Route
        path={ROUTES.ACTIVITY_FEED}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ACTIVITY_FEED].allowedRoles}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ActivityFeedPage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_SUPPORT}
        element={
          <ProtectedRoute
            allowedRoles={routeConfigs[ROUTES.ADMIN_SUPPORT].allowedRoles}
            platformPermission={['platformSupport', 'billingManagement', 'usageReporting', 'tenantManagement']}
          >
            <ErrorBoundary>
              <SupportWorkspacePage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_TENANT_DETAIL}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_TENANT_DETAIL].allowedRoles}>
            <ErrorBoundary>
              <LegacyTenantDetailRedirect />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Tenant Management Page */}
      <Route
        path={ROUTES.ADMIN_TENANTS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_TENANTS].allowedRoles} platformPermission="tenantManagement">
            <ErrorBoundary>
              <TenantManagementPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_BILLING}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_BILLING].allowedRoles} platformPermission="billingManagement">
            <ErrorBoundary>
              <BillingPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_PRICING}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_PRICING].allowedRoles} allowPricing>
            <ErrorBoundary>
              <PricingPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_DEALERS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_DEALERS].allowedRoles} platformPermission="dealerManagement">
            <ErrorBoundary>
              <DealerManagementPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_DEALER_DETAIL}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_DEALER_DETAIL].allowedRoles} platformPermission="dealerManagement">
            <ErrorBoundary>
              <DealerDetailPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_ALL_USERS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_ALL_USERS].allowedRoles}>
            <ErrorBoundary>
              <AllUsersDirectoryPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_BACKUP_RECOVERY}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.ADMIN_BACKUP_RECOVERY].allowedRoles}>
            <ErrorBoundary>
              <BackupRecoveryPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route path={ROUTES.DEALER_WORKSPACE} element={<DealerWorkspacePage />} />
      <Route path={ROUTES.DEALER_WORKSPACE_DEALER} element={<DealerWorkspacePage />} />
      <Route path={ROUTES.DEALER_MANAGE_TENANT} element={<DealerWorkspacePage />} />
      <Route path={ROUTES.DEALER_WORKSPACE_TENANT} element={<DealerWorkspacePage />} />

      {/* Voice & PSTN Settings Page */}
      <Route
        path={ROUTES.VOICE_SETTINGS}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.VOICE_SETTINGS].allowedRoles}>
            <ErrorBoundary>
              <VoiceSettingsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* TwiML Dashboard Page */}
      <Route
        path={ROUTES.VOICE_DASHBOARD}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.VOICE_DASHBOARD].allowedRoles}>
            <ErrorBoundary>
              <VoiceDashboardPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Support Report Detail route - accessible to all authenticated users */}
      <Route
        path={`${ROUTES.SUPPORT_REPORT_DETAIL}/:reportId`}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.MY_REPORTS].allowedRoles}>
            <ErrorBoundary>
              <SupportReportDetailPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Older My Reports links open Support on the caller's own tickets. */}
      <Route
        path={ROUTES.MY_REPORTS}
        element={<Navigate to={`${ROUTES.SUPPORT_REPORTS}?tab=mine`} replace />}
      />

      <Route
        path={ROUTES.SUBSCRIPTION}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.SUBSCRIPTION].allowedRoles}>
            <ErrorBoundary>
              <SubscriptionSummaryPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* My Report Detail route with thread view */}
      <Route
        path={`${ROUTES.MY_REPORT_DETAIL}/:reportId`}
        element={
          <ProtectedRoute allowedRoles={routeConfigs[ROUTES.MY_REPORTS].allowedRoles}>
            <ErrorBoundary>
              <MyReportDetailPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Unauthorized route */}
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Contact Lists Admin Route */}
      <Route
        path={ROUTES.ADMIN_CONTACT_LISTS}
        element={
          <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
            <ErrorBoundary>
              <ContactListsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Collaboration Workspace Admin Routes */}
      <Route
        path="/admin/collaboration-workspaces"
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN, ROLES.OVERSEER]}>
            <ErrorBoundary>
              <CollaborationWorkspaceListPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/collaboration-workspaces/:workspaceId"
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN, ROLES.OVERSEER]}>
            <ErrorBoundary>
              <CollaborationWorkspaceEditorPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Incident Type Admin Routes */}
      <Route
        path="/admin/incidents"
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN, ROLES.OVERSEER]}>
            <ErrorBoundary>
              <IncidentTypeListPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/incidents/:typeKey"
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN, ROLES.OVERSEER]}>
            <ErrorBoundary>
              <IncidentTypeEditorPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Sites Admin Route (Phase 1A) */}
      <Route
        path="/admin/sites"
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN, ROLES.OVERSEER]}>
            <ErrorBoundary>
              <SitesListPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ADMIN_SHORTCUTS}
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <AdminShortcutsPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Home Designer Routes */}
      <Route
        path={ROUTES.ADMIN_WORKSPACES}
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <WorkspaceListPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_WORKSPACE_DETAIL}
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <WorkspaceDetailPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Workspace Studio Routes */}
      <Route
        path={ROUTES.STUDIO}
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <WorkspaceStudioPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.STUDIO_WORKSPACE}
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.TENANT_ADMIN]}>
            <ErrorBoundary>
              <WorkspaceStudioPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Catch-all route */}
      <Route path="*" element={
        !authReady && !skipFirebaseAuthCheck
          ? <LoadingSpinner text="Loading..." />
          : <RootRedirect currentUser={currentUser} agreementStatus={agreementStatus} />
      } />

    </Routes>
  );
};

export default AppRoutes;
